import React, { useState } from 'react';
import Picture from './Picture';
import { filmSrc, mediaUrl, type MediaLang } from './media';

export interface CaptionTrack {
  src: string;
  srclang: MediaLang;
  label: string;
  default?: boolean;
}

interface FilmPlayerProps {
  /** Film base name: `film-de` resolves to `film-de-{720,1080}.mp4`. */
  film: string;
  /** Poster base name with `-{960,1920}` widths. */
  poster: string;
  captions: CaptionTrack[];
  /** Accessible name of the play button; the poster itself carries no text. */
  title: string;
  lang: MediaLang;
}

/**
 * Click-to-play film (implementation brief §4). Self-hosted, so no third-party
 * cookies; the video element only mounts on click, so the page loads the poster
 * and nothing else. 720p below 1280 px viewport width, 1080p above.
 */
const FilmPlayer: React.FC<FilmPlayerProps> = ({ film, poster, captions, title, lang }) => {
  const [playing, setPlaying] = useState(false);

  if (playing) {
    return (
      <div className="hv2-film">
        <video
          className="hv2-film__video"
          src={filmSrc(film, window.innerWidth)}
          poster={mediaUrl(`${poster}-1920.jpg`)}
          controls
          autoPlay
          playsInline
          lang={lang}
          width={1920}
          height={1080}
        >
          {captions.map((c) => (
            <track
              key={c.src}
              kind="captions"
              src={mediaUrl(c.src)}
              srcLang={c.srclang}
              label={c.label}
              default={c.default}
            />
          ))}
        </video>
      </div>
    );
  }

  return (
    <button type="button" className="hv2-film hv2-film__poster" onClick={() => setPlaying(true)} aria-label={title}>
      <Picture name={poster} widths={[960, 1920]} alt="" width={1920} height={1080} sizes="(max-width: 1280px) 100vw, 1280px" />
      <span className="hv2-film__play" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="28" height="28">
          <path d="M8 5v14l11-7z" fill="currentColor" />
        </svg>
      </span>
    </button>
  );
};

export default FilmPlayer;
